import { Field, TextInput } from '@/components/builder/inspector-fields';
import { useAssets } from '@/lib/queries';
import { useContextStore } from '@/stores/context';

// The assets library already existed and the builder could not reach it — the only way to point an
// `image` at anything was to paste a URL, next to an uploads page nobody could get to from here.
//
// The URL field stays: an `image.source` is `{kind:'url', url}` on the wire, and a customer's own CDN
// is a legitimate source. The picker just fills it in.

const CUSTOM = '__custom__';

const basename = (key: string): string => key.split('/').pop() ?? key;

export function AssetField({
  label,
  url,
  error,
  onChange,
}: {
  label: string;
  url: string;
  error?: string | undefined;
  onChange: (url: string) => void;
}): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: assets } = useAssets(appId);
  // A font in an <img> is not a thing. Only image-ish assets are offered.
  const images = (assets ?? []).filter((a) => a.content_type.startsWith('image/'));
  const current = images.find((a) => a.url === url);

  return (
    <>
      <Field label={label} error={error}>
        <TextInput aria-label={label} placeholder="https://…" value={url} onChange={onChange} />
      </Field>
      {images.length > 0 ? (
        <Field label="from assets">
          <select
            aria-label={`${label} asset`}
            className="h-7 w-[150px] min-w-0 rounded-md border bg-transparent px-1.5 text-[12px] outline-none focus:border-indigo-500"
            value={current?.id ?? CUSTOM}
            onChange={(e) => {
              const asset = images.find((a) => a.id === e.target.value);
              if (asset) onChange(asset.url);
            }}
          >
            <option value={CUSTOM}>{current ? 'choose…' : 'custom URL'}</option>
            {images.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {basename(asset.r2_key)}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
    </>
  );
}
